import * as React from 'react';
import { ClientResponseError } from 'pocketbase';
import { pb } from '@/lib/pb';
import { COLLECTIONS, type Settings as SettingsRecord } from '@/lib/types';
import { Btn, Icon } from '@/ui';
import { PageHeader, Card, Spinner, TextField, Checkbox, useToast, pbError } from './ui';

export function Settings() {
  const toast = useToast();
  const [rec, setRec] = React.useState<SettingsRecord | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  // form
  const [recipient, setRecipient] = React.useState('');
  const [prefix, setPrefix] = React.useState('');
  const [notify, setNotify] = React.useState(true);
  const [host, setHost] = React.useState('');
  const [port, setPort] = React.useState('587');
  const [user, setUser] = React.useState('');
  const [pass, setPass] = React.useState('');
  const [tls, setTls] = React.useState(true);
  const [fromEmail, setFromEmail] = React.useState('');
  const [fromName, setFromName] = React.useState('');

  React.useEffect(() => {
    let alive = true;
    void pb.collection(COLLECTIONS.settings).getList<SettingsRecord>(1, 1)
      .then((res) => {
        const s = res.items[0];
        if (!alive || !s) { setLoading(false); return; }
        setRec(s);
        setRecipient(s.contact_recipient); setPrefix(s.contact_subject_prefix);
        setNotify(s.notifications_enabled);
        setHost(s.smtp_host); setPort(String(s.smtp_port || 587)); setUser(s.smtp_user);
        setPass(s.smtp_pass); setTls(s.smtp_tls);
        setFromEmail(s.smtp_from_email); setFromName(s.smtp_from_name);
        setLoading(false);
      })
      .catch((e) => { if (alive) { toast.show(pbError(e), 'error'); setLoading(false); } });
    return () => { alive = false; };
  }, [toast]);

  const payload = () => ({
    contact_recipient: recipient.trim(),
    contact_subject_prefix: prefix,
    notifications_enabled: notify,
    smtp_host: host.trim(),
    smtp_port: Number(port) || 587,
    smtp_user: user.trim(),
    smtp_pass: pass,
    smtp_tls: tls,
    smtp_from_email: fromEmail.trim(),
    smtp_from_name: fromName.trim(),
  });

  const save = async (): Promise<boolean> => {
    if (!rec) { toast.show('Einstellungs-Datensatz fehlt. Bitte „npm run provision" ausführen.', 'error'); return false; }
    setSaving(true);
    try {
      const updated = await pb.collection(COLLECTIONS.settings).update<SettingsRecord>(rec.id, payload());
      setRec(updated);
      toast.show('Einstellungen gespeichert.', 'success');
      return true;
    } catch (e) {
      toast.show(pbError(e), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const ok = await save();
      if (!ok) return;
      await pb.send('/api/aria/test-mail', { method: 'POST' });
      toast.show('Test-E-Mail versendet — bitte Postfach prüfen.', 'success');
    } catch (e) {
      if (e instanceof ClientResponseError && e.status === 404) {
        toast.show('Test-Endpunkt nicht gefunden. Ist der pb_hooks-Mailhook auf dem Server deployt?', 'error');
      } else {
        toast.show(pbError(e), 'error');
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Spinner label="Einstellungen werden geladen…" />;

  return (
    <div>
      <PageHeader
        title="Einstellungen"
        subtitle="Kontaktformular-Versand und SMTP."
        actions={<Btn variant="primary" size="sm" iconLeft="save" onClick={save}>{saving ? 'Speichern…' : 'Speichern'}</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 18, maxWidth: 680 }}>
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 16px' }}>Kontaktformular</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextField label="Zieladresse (Empfänger)" type="email" value={recipient} onChange={setRecipient} placeholder="hallo@aria-robotics.xyz" hint="An diese Adresse gehen neue Kontaktanfragen." />
            <TextField label="Betreff-Präfix" value={prefix} onChange={setPrefix} placeholder="[Kontakt] " />
            <Checkbox label="Benachrichtigungen bei neuen Anfragen senden" checked={notify} onChange={setNotify} />
          </div>
        </Card>

        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 6px' }}>SMTP-Zugang</h2>
          <p style={{ fontSize: 12.5, color: 'var(--text-200)', margin: '0 0 16px', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <Icon name="info" size={15} /> Diese Zugangsdaten werden in der Datenbank gespeichert und für den Versand an die globalen PocketBase-Mail-Settings übergeben.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
            <TextField label="Host" value={host} onChange={setHost} placeholder="smtp.example.com" />
            <TextField label="Port" type="number" value={port} onChange={setPort} placeholder="587" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <TextField label="Benutzer" value={user} onChange={setUser} placeholder="user@example.com" />
            <TextField label="Passwort" type="password" value={pass} onChange={setPass} placeholder="••••••••" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <TextField label="Absender-E-Mail" type="email" value={fromEmail} onChange={setFromEmail} placeholder="noreply@aria-robotics.xyz" />
            <TextField label="Absender-Name" value={fromName} onChange={setFromName} placeholder="Aria Robotics" />
          </div>
          <div style={{ marginTop: 16 }}>
            <Checkbox label="TLS/SSL verwenden" checked={tls} onChange={setTls} />
          </div>
          <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
            <Btn variant="secondary" size="sm" iconLeft="send" onClick={sendTest}>{testing ? 'Sende…' : 'Test-E-Mail senden'}</Btn>
            <span style={{ fontSize: 12.5, color: 'var(--text-200)' }}>Speichert und versendet eine Test-Mail an die Zieladresse.</span>
          </div>
        </Card>
      </div>
    </div>
  );
}
